<template>
    <div v-if="visible" class="banner" :class="`banner--${tone}`">
        <div class="banner-main">
            <div class="banner-title">{{ titleText }}</div>
            <div class="banner-text">{{ message }}</div>
        </div>
        <div class="banner-actions">
            <button
                v-if="primaryCta && primaryCta.action === 'upgrade'"
                type="button"
                class="banner-btn banner-btn--primary"
                @click="$emit('upgrade')"
            >
                {{ primaryCta.label }}
            </button>
            <router-link
                v-else-if="primaryCta && primaryCta.to"
                :to="primaryCta.to"
                class="banner-btn banner-btn--primary"
            >
                {{ primaryCta.label }}
            </router-link>
            <router-link
                v-if="secondaryCta && secondaryCta.to"
                :to="secondaryCta.to"
                class="banner-btn"
            >
                {{ secondaryCta.label }}
            </router-link>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    accessTier: { type: String, required: true },
    remaining: { type: Number, required: true },
    total: { type: Number, required: true },
    isLimitReached: { type: Boolean, default: false },
})

defineEmits(['upgrade'])

const visible = computed(() => {
    if (props.accessTier === 'pro') {
        return false
    }
    return true
})

const tone = computed(() => {
    if (props.isLimitReached) {
        return 'danger'
    }
    if (props.remaining <= 1) {
        return 'warn'
    }
    return 'info'
})

const titleText = computed(() => {
    if (props.isLimitReached) {
        return props.accessTier === 'guest'
            ? 'Лимит бесплатных запросов исчерпан'
            : 'Бесплатные запросы на сегодня закончились'
    }
    if (props.accessTier === 'guest') {
        return `Осталось ${props.remaining} из ${props.total} запросов без входа`
    }
    return `Осталось ${props.remaining} из ${props.total} бесплатных запросов сегодня`
})

const message = computed(() => {
    if (props.isLimitReached) {
        if (props.accessTier === 'guest') {
            return 'Чтобы продолжить, войдите или зарегистрируйтесь — это бесплатно. После регистрации — 10 запросов в день.'
        }
        return 'Откройте больше лимитов и расширенные источники по тарифу. Дневной лимит обновится завтра.'
    }
    if (props.accessTier === 'guest') {
        return 'После регистрации — 10 бесплатных запросов в день и история диалогов.'
    }
    return 'Бесплатный режим. После исчерпания дневного лимита можно расширить лимиты по тарифу.'
})

const primaryCta = computed(() => {
    if (props.accessTier === 'guest') {
        return { label: 'Войти', to: '/login' }
    }
    return { label: 'Выбрать тариф', action: 'upgrade' }
})

const secondaryCta = computed(() => {
    if (props.accessTier === 'guest') {
        return { label: 'Зарегистрироваться', to: '/register-first-step' }
    }
    return null
})
</script>

<style scoped>
.banner {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-radius: 8px;
    margin: 6px auto 0;
    max-width: 760px;
    width: calc(100% - 40px);
    border: 1px solid #E8E6E0;
    background: #FAFAF7;
}

.banner--info {
    background: #F7F5F0;
    border-color: #E8E6E0;
}

.banner--warn {
    background: #FFF6E0;
    border-color: #F2DC9C;
}

.banner--danger {
    background: #FDECEC;
    border-color: #F5C7C7;
}

.banner-main {
    flex: 1;
    min-width: 220px;
}

.banner-title {
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
    margin-bottom: 2px;
}

.banner-text {
    font-size: 12px;
    color: #5F5E5A;
    line-height: 1.5;
}

.banner-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.banner-btn {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-family: inherit;
    padding: 5px 12px;
    border-radius: 6px;
    border: 1px solid #23282C;
    background: white;
    color: #23282C;
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
}

.banner-btn:hover {
    background: #F1EFE8;
}

.banner-btn--primary {
    background: #E30613;
    color: white;
    border-color: #E30613;
}

.banner-btn--primary:hover {
    background: #c30410;
    border-color: #c30410;
}

@media (max-width: 768px) {
    .banner {
        width: calc(100% - 28px);
    }
}
</style>
